import { TaskGet, TaskUserGet, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { store } from "~/core/store";
import { upsert, waitForSignal } from "~/utils";
import { Budget, BudgetFeeLine } from "../budget/interfaces";
import { ApiSliceParams } from "../interfaces";
import { makeStatus } from "../status";
import { createEmitter, createListController, createSubscribe } from "../utils";
import { makeAssignment } from "./assignment";
import { makeCategory } from "./category";
import {
    Task,
    TaskCreateRequest,
    TaskDeleteRequest,
    TaskRelation,
    TaskUpdateRequest,
    TaskUser,
} from "./interfaces";

export const makeTask = ({ connection, company }: ApiSliceParams) => {
    const projectTasks: Record<string, ReadonlySignal<Task[]>> = {};
    const taskEmitter = createEmitter<Task>();

    const loadTaskUser = async (
        id: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(id) ? id : [id];
        const items = con.task.user.get({
            id: finalId,
        });
        const result: TaskUser[] = [];

        for await (const user of items) {
            const finalTaskUser = {
                ...user,
                _assignee: user.assignee,
                _id: user.id,
                id: user.id.toString(),
            };

            result.push(finalTaskUser);
        }

        taskUsers.value = upsert(taskUsers.peek().slice(), result, "id");

        return result;
    };

    const assignment = makeAssignment({ connection, company, loadTaskUser });
    const category = makeCategory({ connection, company });
    const status = makeStatus({ connection, company });

    const {
        items: taskUsers,
        listen: listenTaskUser,
        loading: taskUsersLoading,
    } = createListController<TaskUserGet, TaskUser, string>({
        connection,
        list: (con, taskId) =>
            con.task.user.items({ task: Ulid.fromString(taskId) }),
        getKey: (taskId) => taskId,
        view: (taskUser) => ({
            ...taskUser,
            _assignee: taskUser.assignee,
            _id: taskUser.id,
            id: taskUser.id.toString(),
        }),
        name: "task-user",
    });

    const relations = signal<TaskRelation[]>([]);

    // @TODO: revisit using get instead of items
    const loadTaskRelations = async (ids: Readonly<Ulid>[]) => {
        const con = connection.value;
        const result: TaskRelation[] = [];

        for await (const relation of con.task.relation.get({ id: ids })) {
            result.push({
                ...relation,
                _id: relation.id,
                id: relation.id.toString(),
            });
        }

        relations.value = upsert(relations.peek().slice(), result, "id");

        return result;
    };

    const view = (task: TaskGet) => {
        const finalTask: Task = {
            ...task,
            id: task.id.toString(),
            _id: task.id,
            _reporter: task.reporter ?? null,
            _requestedAssignee: task.requested_assignee ?? null,
            _feeCode: task.activity,
            _project: task.project,
            users: computed(() =>
                taskUsers.value.filter((tu) => tu.task.equals(task.id)),
            ),
            loading: taskUsersLoading(task.id.toString()),
            parents: computed(() =>
                relations.value.filter((r) => r.task.equals(task.id)),
            ),
            children: computed(() =>
                relations.value.filter((r) => r.parent.equals(task.id)),
            ),
            itag: signal(0),
        };

        listenTaskUser(task.id.toString());

        return finalTask;
    };

    const {
        items: tasks,
        listen: listenTasks,
        allLoading,
        allLoaded,
        loading: loadingByProject,
        loaded: loadedByProject,
    } = createListController<TaskGet, Task, string | null>({
        connection,
        list: (con, projectId) =>
            con.task.items({
                company: company.value,
                project: projectId ? Ulid.fromString(projectId) : undefined,
            }),
        processBatch: (batch) => {
            const items = batch.map(view);

            loadTaskRelations(batch.map((item) => item.id));

            return items;
        },
        getKey: (projectId) => projectId ?? "_all",
        view,
        emit: taskEmitter.emit,
        name: "task",
    });

    const loadTask = async (
        taskId: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(taskId) ? taskId : [taskId];
        const list = con.task.get({ id: finalId });
        const result: Task[] = [];

        for await (const task of list) {
            result.push(view(task));
        }

        // @TODO: sort question
        tasks.value = upsert(tasks.peek().slice(), result, "id").sort((a, b) =>
            a.id.localeCompare(b.id),
        );

        return result;
    };

    const create = async (request: TaskCreateRequest) => {
        const con = connection.value;

        return con.task.create({
            activity: request.activity,
            name: request.name,
            description: request.description,
            company: company.value,
            project: request.project,
            reporter: request.reporter,
            budgeted_start: request.budgetedStart,
            budgeted_end: request.budgetedEnd,
            estimate: request.estimate,
            requested_assignee: request.requestedAssignee,
        });
    };

    const update = async (request: TaskUpdateRequest) => {
        const con = connection.value;
        const existing = tasks.value.find((t) => t._id.equals(request.id));

        return con.task.update({
            etag: request.etag,
            id: request.id,
            activity:
                request.activity && existing?._feeCode.equals(request.activity)
                    ? undefined
                    : request.activity,
            name: existing?.name === request.name ? undefined : request.name,
            description:
                existing?.description === request.description
                    ? undefined
                    : request.description,
            reporter:
                request.reporter &&
                existing?._reporter?.equals(request.reporter)
                    ? undefined
                    : request.reporter,
            budgeted_start:
                existing?.budgeted_start === request.budgetedStart
                    ? undefined
                    : request.budgetedStart,
            budgeted_end:
                existing?.budgeted_end === request.budgetedEnd
                    ? undefined
                    : request.budgetedEnd,
            estimate:
                existing?.estimate === request.estimate
                    ? undefined
                    : request.estimate,
            resource_plan:
                existing?.resource_plan === request.resourcePlan
                    ? undefined
                    : request.resourcePlan,
            requested_assignee:
                request.requestedAssignee &&
                existing?._requestedAssignee?.equals(request.requestedAssignee)
                    ? undefined
                    : request.requestedAssignee,
        });
    };

    const remove = async (request: TaskDeleteRequest) => {
        const con = connection.value;

        return con.task.delete({
            etag: request.etag,
            id: request.id,
        });
    };

    const cloneProject = async (source: string, target: string) => {
        if (source === target) {
            return;
        }

        listenTasks(source);

        const loadedSignal = loadedByProject(source, true);

        await waitForSignal(loadedSignal, true);

        const projectTasks = tasks
            .peek()
            .filter((t) => t._project.toString() === source);
        const promises: Promise<unknown>[] = [];

        for (const task of projectTasks) {
            const feeCode = task._feeCode;
            const reporter = task._reporter;

            if (!feeCode || !reporter) {
                continue;
            }

            promises.push(
                create({
                    activity: feeCode,
                    budgetedEnd: task.budgeted_end,
                    budgetedStart: task.budgeted_start,
                    description: task.description,
                    estimate: task.estimate,
                    name: task.name,
                    project: Ulid.fromString(target),
                    reporter,
                    requestedAssignee: task.requested_assignee,
                }),
            );
        }

        await Promise.all(promises);
    };

    const fromBudget = async (budget: Budget, projectId: Readonly<Ulid>) => {
        const { api } = store;
        const fees = budget.phases
            .peek()
            .flatMap((item) =>
                item.lines
                    .peek()
                    .filter((item): item is BudgetFeeLine => item.t === "A"),
            );

        if (fees.length === 0) {
            return [0, 0] as const;
        }

        const activities = api.feeCode.codes.peek();
        const employees = api.employee.employees.peek();
        const me = api.employee.me.peek();

        if (!me) {
            return [0, 0] as const;
        }

        const promises: Promise<unknown>[] = [];

        for (const fee of fees) {
            const activity = activities.find((item) =>
                fee.data.fee_code?.equals(item._id),
            );
            const employee = employees.find((item) =>
                fee.data.employee?.equals(item._id),
            );

            if (!activity) {
                continue;
            }

            promises.push(
                api.task.create({
                    activity: activity._id,
                    description: "",
                    name: fee.data.description ?? "",
                    project: projectId,
                    reporter: me._id,
                    budgetedStart: fee.data.date_start,
                    budgetedEnd: fee.data.date_end,
                    estimate: fee.data.hours.toNumber() * 3600,
                    requestedAssignee: employee?._id,
                }),
            );
        }

        const result = await Promise.allSettled(promises);
        const successCount = result.filter(
            (i) => i.status === "fulfilled",
        ).length;
        const failCount = result.filter((i) => i.status === "rejected").length;

        return [successCount, failCount] as const;
    };

    const updateStatus = async (
        _: Task, // @TODO: consider removing
        taskUser: TaskUser,
        status: Readonly<Ulid>,
    ) => {
        const con = connection.value;

        return con.task.user.update({
            etag: taskUser.etag,
            id: taskUser._id,
            status,
        });
    };

    const getTask = async (id: Readonly<Ulid>) => {
        const task = tasks.value.find((t) => t._id.equals(id));

        if (task) {
            return task;
        }

        const newTask = await loadTask(id);

        if (newTask.length) {
            return newTask[0];
        }

        throw new Error("Task not found");
    };

    const byProject = (id: string) => {
        if (!(id in projectTasks)) {
            projectTasks[id] = computed(() =>
                tasks.value.filter((t) => t._project.toString() === id),
            );
            listenTasks(id);
        }

        return projectTasks[id];
    };

    const subscribe = createSubscribe(
        tasks,
        connection,
        (con) => con.task.subscribe(),
        loadTask,
    );

    const subscribeTaskUser = createSubscribe(
        taskUsers,
        connection,
        (con) => con.task.user.subscribe(),
        loadTaskUser,
    );

    return {
        tasks: byProject,
        loading: loadingByProject,

        create,
        update,
        remove,

        on: taskEmitter.on,
        off: taskEmitter.off,

        all: tasks,
        allLoading,
        allLoaded,

        listenAll: () => listenTasks(null),
        cloneProject,
        fromBudget,

        assignment,
        taskUsers,
        loadTaskUser,
        getTask,
        updateStatus,

        category,
        status,

        // @TODO: double check if these subscriptions needed
        subscribe,
        subscribeTaskUser,
    };
};
